import { computed, ref, watch, type Ref } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import { useGuardianChildrenQuery } from '@/types/vue-apollo-helpers';
import type { GuardianChildrenQuery } from '@/types/operations';
import { resolveSelectedChildId } from '@/utils/guardianPortal';

export type GuardianChild = GuardianChildrenQuery['guardianChildren'][number];

export const SELECTED_CHILD_STORAGE_KEY = 'guardian_selected_child';

function readStoredChildId(): string {
  try {
    return sessionStorage.getItem(SELECTED_CHILD_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function storeChildId(id: string) {
  try {
    sessionStorage.setItem(SELECTED_CHILD_STORAGE_KEY, id);
  } catch {
    // Storage unavailable (private mode) — the selection just won't persist.
  }
}

/**
 * The guardian's children and which one the portal is showing. A stored id
 * that is no longer one of the children (unlinked, other account, other
 * school) is replaced by the first child, so the switcher never renders blank.
 */
export function useGuardianChildSelection() {
  const { result, loading, error, refetch } = useGuardianChildrenQuery();
  const children = computed<GuardianChild[]>(() => result.value?.guardianChildren ?? []);
  const selectedChildId = ref(readStoredChildId());

  // Registered first so a normalisation on the first (cached) result is stored too.
  watch(selectedChildId, (id) => {
    if (id) storeChildId(id);
  });

  watch(children, (list) => {
    if (!list.length) return;
    const resolved = resolveSelectedChildId(list, selectedChildId.value);
    if (resolved !== selectedChildId.value) selectedChildId.value = resolved;
  }, { immediate: true });

  const selectedChild = computed<GuardianChild | null>(() =>
    children.value.find(c => String(c.student.id) === selectedChildId.value) ?? null,
  );

  return { children, selectedChildId, selectedChild, loading, error, refetch };
}

/** The parts of the portal context a single-child page follows. */
export interface ChildSwitchContext {
  children: Ref<ReadonlyArray<{ student: { id: string } }>>;
  selectedChildId: Ref<string>;
  childId: Ref<string>;
}

/**
 * For pages about one specific child's record (e.g. a bill). On arrival the
 * switcher is moved to the page's child, so a deep link to the second child's
 * bill shows that child in the banner instead of bouncing to the first
 * child's list. After that, a switch to another child no longer matches the
 * page, so it goes back to the list for the newly selected child.
 */
export function useLeaveOnChildSwitch(context: ChildSwitchContext, pageStudentId: Ref<string>, listRoute: RouteLocationRaw) {
  const router = useRouter();
  let adopted = false;

  watch([context.children, pageStudentId], ([list, pageId]) => {
    if (adopted || !list.length) return;
    adopted = true;
    if (list.some(c => String(c.student.id) === String(pageId))) context.selectedChildId.value = String(pageId);
  }, { immediate: true });

  watch(context.childId, (id, previous) => {
    if (!adopted || !id || !previous || id === previous || id === pageStudentId.value) return;
    router.replace(listRoute);
  });
}
